import { useCallback, useEffect, useRef, useState } from 'react';
import {
  onboardingAudioCaptureService,
  type OnboardingTranscriptionResult,
} from '../services/onboardingAudioCaptureService';

export type VoiceTranscriptState = 'idle' | 'listening';

/**
 * Owns the complete microphone lifecycle for conversational onboarding.
 * Voice only ever populates the same editable text field as typing; it never
 * submits on the user's behalf.
 */
export const useVoiceTranscript = (
  onTranscriptChange: (transcript: string) => void,
) => {
  const [state, setState] = useState<VoiceTranscriptState>('idle');
  const [error, setError] = useState<string | null>(null);
  const finalChunksRef = useRef<string[]>([]);

  const stop = useCallback(() => {
    onboardingAudioCaptureService.stopCapture();
    setState('idle');
  }, []);

  const reset = useCallback(() => {
    stop();
    finalChunksRef.current = [];
    setError(null);
  }, [stop]);

  const applyResult = useCallback((result: OnboardingTranscriptionResult) => {
    if (result.isFinal && result.transcript.trim()) {
      finalChunksRef.current.push(result.transcript.trim());
    }
    const interim = result.isFinal ? '' : result.transcript;
    onTranscriptChange([...finalChunksRef.current, interim].filter(Boolean).join(' '));
  }, [onTranscriptChange]);

  const toggle = useCallback(async () => {
    if (state === 'listening') {
      stop();
      return;
    }

    setError(null);
    finalChunksRef.current = [];
    try {
      await onboardingAudioCaptureService.startCapture(applyResult);
      setState('listening');
    } catch (cause) {
      setState('idle');
      setError(cause instanceof Error ? cause.message : 'Could not start the microphone.');
    }
  }, [applyResult, state, stop]);

  useEffect(() => {
    onboardingAudioCaptureService.onConnectionLost(() => {
      setState('idle');
      setError('Voice connection was lost. Your transcript is still here; continue typing or try the mic again.');
    });
    return () => {
      onboardingAudioCaptureService.onConnectionLost(() => undefined);
      onboardingAudioCaptureService.stopCapture();
    };
  }, []);

  return { state, error, toggle, stop, reset };
};
